package ai.wagemix.logic.account

import ai.wagemix.logic.entitledPlan
import ai.wagemix.logic.isLapsed
import ai.wagemix.net.models.WMAccountRules
import ai.wagemix.data.touchesUser
import ai.wagemix.data.WMAuthSyncEvent
import ai.wagemix.data.WMBillingSyncEvent
import ai.wagemix.logic.account.WMAccountPresenter.Action
import ai.wagemix.logic.account.WMAccountPresenter.Event
import ai.wagemix.logic.account.WMAccountPresenter.Prompt
import ai.wagemix.logic.jobdescriptions.WMUploadQuota
import ai.wagemix.logic.sendWMPasswordResetRequested
import ai.wagemix.logic.session
import ai.wagemix.logic.storedMixesFlow
import ai.wagemix.logic.subscription
import ai.wagemix.logic.userData
import ai.wagemix.logic.WMNavigationRequest
import ai.wagemix.logic.WMPlanRules
import ai.wagemix.net.models.fromJobDescription
import ai.wagemix.net.models.WMSubscriptionInfo
import com.fsryan.foundation.logic.FSPresenter
import com.fsryan.foundation.logic.FSPresenter.Event.NullableValueWrapper
import com.fsryan.foundation.logic.FSPrompt
import com.fsryan.foundation.logic.NullableValueWrapper
import com.fsryan.foundation.logic.presenterInstanceId
import com.fsryan.foundation.logic.prompting.defaultHandlePromptResponse
import com.fsryan.foundation.logic.prompting.FSBasePromptingPresenter
import com.fsryan.foundation.logic.prompting.FSPromptingPresenter
import com.fsryan.foundation.models.FSPropertyContainer
import com.fsryan.foundation.util.log.FSLog
import kotlin.jvm.JvmInline
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.onStart

data class WMProfile(val firstName: String, val lastName: String, val email: String, val emailVerified: Boolean)

/**
 * The names the profile form holds that are longer than [limit] ([WMAccountRules.MAX_NAME_LENGTH], which the Firestore
 * rules and the API enforce too): each length is the trimmed name's when it is too long, `null` when it fits.
 */
@ConsistentCopyVisibility
data class WMProfileNameProblems internal constructor(val limit: Int, val firstNameLength: Int?, val lastNameLength: Int?) {
    val any: Boolean get() = firstNameLength != null || lastNameLength != null

    companion object {
        /** Whether the trimmed [firstName] and [lastName] fit [WMAccountRules.MAX_NAME_LENGTH]. */
        fun check(firstName: String, lastName: String): WMProfileNameProblems {
            val limit = WMAccountRules.MAX_NAME_LENGTH
            val first = firstName.trim().length.takeIf { it > limit }
            val last = lastName.trim().length.takeIf { it > limit }
            return WMProfileNameProblems(limit, first, last)
        }
    }
}

/** Account: the profile form, password reset link, subscription card, quota and billing portal. */
interface WMAccountPresenter<P: Prompt, out E: Event<P>, in A: Action>: FSPromptingPresenter<P, E, A> {

    interface Action: FSPromptingPresenter.Action {
        sealed interface Base: Action

        @JvmInline
        value class ComposedSuperAction internal constructor(val wrapped: FSPromptingPresenter.Action.Base): Base

        @ConsistentCopyVisibility
        data class EditProfile internal constructor(val firstName: String, val lastName: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<EditProfile> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class SaveProfile internal constructor(val firstName: String, val lastName: String, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SaveProfile> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class SendPasswordReset internal constructor(override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<SendPasswordReset> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        @ConsistentCopyVisibility
        data class OpenBillingPortal internal constructor(val returnPath: String?, override val extendedProperties: Map<String, Any> = emptyMap()): Base, FSPropertyContainer<OpenBillingPortal> {
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(extendedProperties = properties)
        }

        interface Factory<P: Prompt, out A: Action>: FSPromptingPresenter.Action.Factory<P, A> {
            fun createEditProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSaveProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any> = emptyMap()): A
            fun createSendPasswordReset(extendedProperties: Map<String, Any> = emptyMap()): A
            fun createOpenBillingPortal(returnPath: String?, extendedProperties: Map<String, Any> = emptyMap()): A
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base> = object: Factory<P, Base> {
                private val base = FSPromptingPresenter.Action.baseFactory<P>()
                override fun createUserRespondedToPrompt(response: FSPrompt.Response<P>, extendedProperties: Map<String, Any>): Base = ComposedSuperAction(base.createUserRespondedToPrompt(response, extendedProperties))
                override fun createEditProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any>): Base = EditProfile(firstName, lastName, extendedProperties)
                override fun createSaveProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any>): Base = SaveProfile(firstName, lastName, extendedProperties)
                override fun createSendPasswordReset(extendedProperties: Map<String, Any>): Base = SendPasswordReset(extendedProperties)
                override fun createOpenBillingPortal(returnPath: String?, extendedProperties: Map<String, Any>): Base = OpenBillingPortal(returnPath, extendedProperties)
            }
        }
    }

    interface Event<out P: Prompt>: FSPromptingPresenter.Event<P> {
        val profileUpdate: WMProfile?
        val saveAllowedUpdate: Boolean?
        val subscriptionUpdate: NullableValueWrapper<WMSubscriptionInfo?>?
        val quotaUpdate: WMUploadQuota?
        val resetLinkSentUpdate: Boolean?
        val navigationUpdate: WMNavigationRequest?
        /** Which names in the form are too long (Penpot `Account / Name Too Long`); none when the profile loads. */
        val nameProblemsUpdate: WMProfileNameProblems?

        @ConsistentCopyVisibility
        data class Base<out P: Prompt> internal constructor(
            private val base: FSPromptingPresenter.Event.Base<P>,
            override val profileUpdate: WMProfile? = null,
            override val saveAllowedUpdate: Boolean? = null,
            override val subscriptionUpdate: NullableValueWrapper<WMSubscriptionInfo?>? = null,
            override val quotaUpdate: WMUploadQuota? = null,
            override val resetLinkSentUpdate: Boolean? = null,
            override val navigationUpdate: WMNavigationRequest? = null,
            override val nameProblemsUpdate: WMProfileNameProblems? = null
        ): Event<P>, FSPropertyContainer<Base<P>>, FSPromptingPresenter.Event<P> by base {
            override val extendedProperties: Map<String, Any> get() = base.extendedProperties
            override fun replaceExtendedProperties(properties: Map<String, Any>) = copy(base = base.replaceExtendedProperties(properties))
        }

        interface Factory<P: Prompt, out E: Event<P>>: FSPromptingPresenter.Event.Factory<P, E> {
            fun createProfileUpdate(profile: WMProfile): E
            fun createSaveAllowed(allowed: Boolean): E
            fun createSubscriptionUpdate(subscription: WMSubscriptionInfo?): E
            fun createQuotaUpdate(quota: WMUploadQuota): E
            fun createResetLinkSent(sent: Boolean): E
            fun createNavigation(request: WMNavigationRequest): E
            fun createNameProblems(problems: WMProfileNameProblems): E
        }

        companion object {
            fun <P: Prompt> baseFactory(): Factory<P, Base<P>> = object: Factory<P, Base<P>> {
                private val base = FSPromptingPresenter.Event.baseFactory<P>()
                override fun createPromptChange(prompt: P, loading: Boolean?, extendedProperties: Map<String, Any>) = Base(base.createPromptChange(prompt, loading, extendedProperties))
                override fun createLoadingChange(loading: Boolean, extendedProperties: Map<String, Any>) = Base(base.createLoadingChange(loading, extendedProperties))
                override fun none() = Base(base.none())
                override fun createProfileUpdate(profile: WMProfile) = Base(base.none(), profileUpdate = profile)
                override fun createSaveAllowed(allowed: Boolean) = Base(base.none(), saveAllowedUpdate = allowed)
                override fun createSubscriptionUpdate(subscription: WMSubscriptionInfo?) = Base(base.none(), subscriptionUpdate = NullableValueWrapper(subscription))
                override fun createQuotaUpdate(quota: WMUploadQuota) = Base(base.none(), quotaUpdate = quota)
                override fun createResetLinkSent(sent: Boolean) = Base(base.none(), resetLinkSentUpdate = sent)
                override fun createNameProblems(problems: WMProfileNameProblems) = Base(base.none(), nameProblemsUpdate = problems)
                override fun createNavigation(request: WMNavigationRequest) = Base(base.none(), navigationUpdate = request)
            }
        }
    }

    interface Prompt: FSPromptingPresenter.Prompt {
        sealed interface Base: Prompt

        @JvmInline
        value class ComposedSuperPrompt internal constructor(val wrapped: FSPromptingPresenter.Prompt.Base): Base

        data object ProfileSaved: Base, FSPrompt.Displayed {
            override val forceInteraction = false
            override val messageOverride: String? = null
            override val titleOverride: String? = null
        }

        interface Factory<out P: Prompt>: FSPromptingPresenter.Prompt.Factory<P> {
            fun profileSaved(): P
        }

        companion object {
            fun baseFactory(): Factory<Base> = object: Factory<Base> {
                override fun createBase(wrapped: FSPrompt.Base): Base = ComposedSuperPrompt(FSPromptingPresenter.Prompt.baseFactory().createBase(wrapped))
                override fun profileSaved(): Base = ProfileSaved
            }
        }
    }

    fun userEditedProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedSaveProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any> = emptyMap())
    fun userRequestedPasswordReset(extendedProperties: Map<String, Any> = emptyMap())
    /** [returnPath] is where the portal sends the user back; the API turns it into a full URL for its environment. */
    fun userRequestedBillingPortal(returnPath: String?, extendedProperties: Map<String, Any> = emptyMap())

    companion object
}

abstract class WMBaseAccountPresenter<
    P: Prompt,
    E: Event<P>,
    A: Action,
    EF: Event.Factory<P, E>,
    AF: Action.Factory<P, A>,
    PF: Prompt.Factory<P>,
    G: WMAccountDataGateway
>(
    protected val dataGateway: G,
    eventFactory: EF,
    actionFactory: AF,
    promptFactory: PF,
    cancelViewScopeOnUnready: Boolean,
    instanceId: String,
    handlePromptResponse: suspend (FSPrompt.Response<P>, suspend E.() -> Unit) -> Unit = { response, emit ->
        context(eventFactory, promptFactory) { defaultHandlePromptResponse(response, emit) }
    }
): FSBasePromptingPresenter<P, E, A, EF, AF, PF>(cancelViewScopeOnUnready, instanceId, eventFactory, actionFactory, promptFactory, handlePromptResponse),
    WMAccountPresenter<P, E, A> {

    override fun userEditedProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createEditProfile(firstName, lastName, extendedProperties))
    override fun userRequestedSaveProfile(firstName: String, lastName: String, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSaveProfile(firstName, lastName, extendedProperties))
    override fun userRequestedPasswordReset(extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createSendPasswordReset(extendedProperties))
    override fun userRequestedBillingPortal(returnPath: String?, extendedProperties: Map<String, Any>) = onUserAction(actionFactory.createOpenBillingPortal(returnPath, extendedProperties))

    @OptIn(ExperimentalCoroutinesApi::class)
    override fun registerSubscriptions() {
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .onEach { emitProfile() }
            .launchInViewScope()

        // The subscription card and the quota: the plan comes with the user slice, the usage from the mirrored mixes.
        dataGateway.cacheUpdateFlow
            .filter { it.touchesUser() }
            .map { dataGateway.session()?.uid }
            .onStart { emit(dataGateway.session()?.uid) }
            .distinctUntilChanged()
            .flatMapLatest { uid -> dataGateway.storedMixesFlow(uid).map { mixes -> mixes.count { it.fromJobDescription } } }
            .flatMapLatest { used -> dataGateway.cacheUpdateFlow.filter { it.touchesUser() }.map { used }.onStart { emit(used) } }
            .onEach { used ->
                // A lapsed account's card shows no subscription, as before it renews; the plan it would renew stays.
                val subscription = dataGateway.subscription().takeUnless { dataGateway.isLapsed() }
                val plan = dataGateway.entitledPlan()
                eventFactory.createSubscriptionUpdate(subscription).emit()
                eventFactory.createQuotaUpdate(WMUploadQuota(used = used, quota = WMPlanRules.uploadQuota(plan), plan = plan)).emit()
            }
            .launchInViewScope()

        dataGateway.authSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMAuthSyncEvent.UpdateProfile.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMAuthSyncEvent.SendPasswordReset.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMAuthSyncEvent.UpdateProfile.Success -> eventFactory.createPromptChange(promptFactory.profileSaved(), loading = false).emit()
                    is WMAuthSyncEvent.SendPasswordReset.Success -> {
                        FSLog.sendWMPasswordResetRequested()
                        eventFactory.createLoadingChange(false).emit()
                        eventFactory.createResetLinkSent(true).emit()
                    }
                    is WMAuthSyncEvent.UpdateProfile.Failure, is WMAuthSyncEvent.SendPasswordReset.Failure -> {
                        eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()

        dataGateway.billingSyncEventFlow
            .filter { it.presenterInstanceId() == instanceId }
            .onEach { event ->
                when (event) {
                    is WMBillingSyncEvent.OpenBillingPortal.Initial -> eventFactory.createLoadingChange(true).emit()
                    is WMBillingSyncEvent.OpenBillingPortal.Success -> {
                        eventFactory.createLoadingChange(false).emit()
                        // The sync put the portal URL into the global context; follow it once and forget it.
                        // `billing_portal_opened` is the API's, which made the session (analytics plan §5a).
                        dataGateway.retrieveGlobalContext().billingPortalUrl()?.let { url ->
                            dataGateway.deleteBillingPortalUrl()
                            eventFactory.createNavigation(WMNavigationRequest.External(url)).emit()
                        }
                    }
                    is WMBillingSyncEvent.OpenBillingPortal.Failure -> {
                        eventFactory.createPromptChange(promptFactory.networkError(), loading = false).emit()
                    }
                    else -> Unit
                }
            }
            .launchInViewScope()
    }

    override suspend fun sendInitialEvents(isFirstOnReadyCall: Boolean) {
        emitProfile()
        eventFactory.createResetLinkSent(false).emit()
    }

    private suspend fun emitProfile() {
        val session = dataGateway.session() ?: return
        val userData = dataGateway.userData()
        eventFactory.createProfileUpdate(
            WMProfile(
                firstName = userData?.firstName ?: session.displayName?.substringBefore(' ') ?: "",
                lastName = userData?.lastName ?: session.displayName?.substringAfter(' ', "") ?: "",
                email = userData?.email?.ifBlank { null } ?: session.email,
                emailVerified = session.emailVerified
            )
        ).emit()
        eventFactory.createSaveAllowed(false).emit()
        eventFactory.createNameProblems(WMProfileNameProblems.check(firstName = "", lastName = "")).emit()
    }

    override fun extractBasePromptingPresenterAction(action: A): FSPromptingPresenter.Action.Base? = (action as? Action.ComposedSuperAction)?.wrapped

    @Suppress("UNCHECKED_CAST")
    override fun FSPrompt.Response<*>.extract(): FSPrompt.Response<P> = this as FSPrompt.Response<P>

    override fun handleNonBasePromptingPresenterAction(action: A) {
        when (action) {
            is Action.EditProfile -> launch {
                val userData = dataGateway.userData()
                val changed = action.firstName.trim() != (userData?.firstName ?: "") || action.lastName.trim() != (userData?.lastName ?: "")
                val problems = WMProfileNameProblems.check(action.firstName, action.lastName)
                eventFactory.createNameProblems(problems).emit()
                val filled = action.firstName.isNotBlank() && action.lastName.isNotBlank()
                eventFactory.createSaveAllowed(filled && changed && !problems.any).emit()
            }
            is Action.SaveProfile -> launch {
                if (action.firstName.isBlank() || action.lastName.isBlank()) {
                    return@launch
                }
                // The rules and the API refuse a name past the limit; the form already says so.
                if (WMProfileNameProblems.check(action.firstName, action.lastName).any) {
                    return@launch
                }
                val uid = dataGateway.session()?.uid ?: return@launch
                dataGateway.updateProfile(uid, action.firstName.trim(), action.lastName.trim(), createPresenterContext(action))
            }
            is Action.SendPasswordReset -> launch {
                val email = dataGateway.session()?.email ?: return@launch
                dataGateway.sendPasswordReset(email, createPresenterContext(action))
            }
            is Action.OpenBillingPortal -> dataGateway.openBillingPortal(action.returnPath, createPresenterContext(action))
            else -> logUnknownActionType(action)
        }
    }
}

typealias WMDefaultAccountPresenter = WMAccountPresenter<Prompt.Base, Event.Base<Prompt.Base>, Action.Base>

fun WMAccountPresenter.Companion.createDefault(
    dataGateway: WMAccountDataGateway,
    instanceId: String = FSPresenter.createInstanceId("WMAccountPresenter")
): WMDefaultAccountPresenter = object: WMBaseAccountPresenter<
    Prompt.Base,
    Event.Base<Prompt.Base>,
    Action.Base,
    Event.Factory<Prompt.Base, Event.Base<Prompt.Base>>,
    Action.Factory<Prompt.Base, Action.Base>,
    Prompt.Factory<Prompt.Base>,
    WMAccountDataGateway
>(
    dataGateway = dataGateway,
    eventFactory = Event.baseFactory(),
    actionFactory = Action.baseFactory(),
    promptFactory = Prompt.baseFactory(),
    cancelViewScopeOnUnready = true,
    instanceId = instanceId
) {}
